// Persona / workspace model — single source of truth for the three-workspace IA.
// Mirrors Docs/Design Handoff/portal/shell.jsx (NAV/HOME/ROUTE_LABEL) and the
// README's WORKSPACE_ACCESS role-gating. Persona accents are SECONDARY colours;
// the brand indigo --accent stays the product primary everywhere else.

export type PersonaKey = 'sales' | 'ops' | 'audit';

// Configurable-RBAC permissions blob (roles.permissions jsonb, migration 29).
// Visibility/convenience keys only — view.<persona> gate workspace VISIBILITY and
// `mutate` gates the canMutate UI convenience. NEVER a security boundary: the
// server stays requireAdmin / is_admin. `null` = "no data" → fall back to the
// hardcoded WORKSPACE_ACCESS + isAdmin below (net-zero / reversible).
export type RolePermissions = Record<string, boolean>;

// Roles come from backend/src/types UserRole.
export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'viewer'
  | 'owner'
  | 'member'
  | 'billing';

export interface Persona {
  key: PersonaKey;
  label: string;
  accent: string; // hex (for inline styles); also available as --persona-* CSS vars
  tint: string;
  icon: string; // lucide name (resolved via shared <Icon/>)
  sub: string;
  home: string; // route the workspace lands on
}

export const PERSONA: Record<PersonaKey, Persona> = {
  sales: { key: 'sales', label: 'Sales', accent: '#059669', tint: '#ECFDF5', icon: 'trending-up', sub: 'Revenue & Growth', home: '/sales' },
  ops:   { key: 'ops',   label: 'Operations', accent: '#0891B2', tint: '#ECFEFF', icon: 'server', sub: 'Fleet & Security', home: '/ops' },
  audit: { key: 'audit', label: 'Audit', accent: '#7C3AED', tint: '#F5F3FF', icon: 'shield-check', sub: 'Compliance & Evidence', home: '/audit' },
};

export const PERSONA_ORDER: PersonaKey[] = ['sales', 'ops', 'audit'];

// Map UserRole → workspace(s) the user may enter (README §Role gating).
// super_admin/admin see all three; everyone else is scoped to one.
export const WORKSPACE_ACCESS: Record<UserRole, PersonaKey[]> = {
  super_admin: ['sales', 'ops', 'audit'],
  admin:       ['sales', 'ops', 'audit'],
  billing:     ['sales'],
  owner:       ['sales'],
  member:      ['ops'],
  viewer:      ['audit'],
};

export const HOME: Record<PersonaKey, string> = {
  sales: PERSONA.sales.home,
  ops: PERSONA.ops.home,
  audit: PERSONA.audit.home,
};

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  /**
   * Hide this item from anyone who is not a platform operator (is_admin).
   *
   * Workspace visibility alone is not enough for every surface: `member` gets
   * the whole Ops workspace via WORKSPACE_ACCESS, but the report/triage queue
   * reads the platform-wide support_tickets inbox — every surface's reports,
   * every reporter's identity — which a member has no business being pointed
   * at. Sending them to a page that can only refuse them is a worse experience
   * than not offering it.
   *
   * NOT A SECURITY BOUNDARY. Same rule as canMutate above: this only decides
   * what the sidebar draws. The licence backend re-authorises the reads behind
   * the page on every request, and the flat /support/tickets route stays
   * reachable by URL regardless of what is rendered here.
   */
  staffOnly?: boolean;
}
export interface NavSection {
  group: string;
  items: NavItem[];
}

/**
 * A20 W7a — the report/triage queue's durable home in the NAV.
 *
 * WHY IT APPEARS IN ALL THREE WORKSPACES. Its route is FLAT (/support/tickets,
 * not /<persona>/...) and must stay that way: every GitHub issue the reporter
 * files carries a `<console>/support/tickets/<id>` deep link
 * (backend/src/services/github-issues.service.ts), so the path is a contract
 * with issue bodies already sitting in the tracker. personaForPath() therefore
 * returns null for it and the chrome falls back to whichever workspace the user
 * last landed in — so an entry pinned to one persona would simply be missing
 * for a staff user working in either of the other two.
 *
 * That is safe precisely because of `staffOnly`: the only people who see it are
 * is_admin, and the workspaces an admin holds are decided elsewhere
 * (WORKSPACE_ACCESS, or view.<persona> once the permission matrix is
 * populated). Listing the section in all three therefore widens nobody's
 * access — it just means the entry is wherever that same audience already is.
 * Licenses and Validations are repeated across workspaces for the same reason:
 * the surface is not workspace-scoped.
 *
 * Before this entry existed the page was reachable only by pasting a URL or via
 * a stop-gap icon in AdminHeader.
 */
const SUPPORT_NAV: NavSection = {
  group: 'Support',
  items: [
    { label: 'Report Triage', path: '/support/tickets', icon: 'life-buoy', staffOnly: true },
    // A20 W8 — the walkthrough library. Flat for the same reason as triage: it
    // is not workspace-scoped, and personaForPath() returns null for it.
    //
    // `staffOnly` (is_admin) is the RIGHT gate for this ENTRY even though
    // RECORDING needs more than that. The page has two halves: the library,
    // which GET /help-assets serves to any admin, and the recorder, which
    // mayAuthorWalkthroughs() gates and assertStudioAuthor enforces on the
    // server. An admin who is not a super-admin gets a real, readable page —
    // so the entry is not a dead end for them — and simply no record button.
    { label: 'Walkthrough Studio', path: '/support/studio', icon: 'clapperboard', staffOnly: true },
  ],
};

// Per-persona navigation (replaces the single flat AdminSidebar.navSections).
export const NAV: Record<PersonaKey, NavSection[]> = {
  sales: [
    { group: 'Revenue', items: [
      { label: 'Overview', path: '/sales', icon: 'trending-up' },
      { label: 'Account Console', path: '/sales/console', icon: 'wallet' },
    ]},
    { group: 'Customers', items: [
      { label: 'Accounts', path: '/sales/accounts', icon: 'building-2' },
      { label: 'Renewals & Churn', path: '/sales/renewals', icon: 'life-buoy' },
      { label: 'Trials', path: '/sales/trials', icon: 'gift' },
    ]},
    { group: 'Licensing', items: [
      { label: 'Licenses', path: '/sales/licenses', icon: 'key' },
      { label: 'Issue License', path: '/sales/licenses/new', icon: 'file-plus-2' },
      { label: 'Validations', path: '/sales/validations', icon: 'badge-check' },
    ]},
    { group: 'Pricing', items: [
      { label: 'License Matrix', path: '/sales/pricing', icon: 'table-2' },
      { label: 'Quotes', path: '/sales/quotes', icon: 'file-text' },
      { label: 'Rate Card Admin', path: '/sales/pricing-admin', icon: 'sliders-horizontal' },
    ]},
    // Trust SaaS commercial surface (18H §4.2 NEW console pages).
    { group: 'Trust SaaS', items: [
      { label: 'Concierge Queue', path: '/sales/trust/leads', icon: 'inbox' },
      { label: 'Org Metrics', path: '/sales/trust/org-metrics', icon: 'bar-chart-3' },
      { label: 'Opportunities', path: '/sales/trust/opportunities', icon: 'trending-up' },
      { label: 'Commercial Contacts', path: '/sales/trust/contacts', icon: 'contact' },
      { label: 'Nudge Matrix', path: '/sales/trust/nudge-matrix', icon: 'bell' },
    ]},
    SUPPORT_NAV,
  ],
  ops: [
    { group: 'Operations', items: [
      { label: 'Overview', path: '/ops', icon: 'layout-dashboard' },
      { label: 'Fleet', path: '/ops/fleet', icon: 'server' },
      { label: 'Remote Operations', path: '/ops/remote-ops', icon: 'terminal' },
    ]},
    { group: 'Security', items: [
      { label: 'Vulnerabilities', path: '/ops/vulnerabilities', icon: 'shield-alert' },
      { label: 'Updates & Releases', path: '/ops/updates', icon: 'cloud-upload' },
      { label: 'Maintenance', path: '/ops/maintenance', icon: 'wrench' },
    ]},
    { group: 'Licensing', items: [
      { label: 'Licenses', path: '/ops/licenses', icon: 'key' },
      { label: 'Validations', path: '/ops/validations', icon: 'badge-check' },
      // OF-170: answers an air-gapped install's challenge with a signed response
      // (it hands out a licence key, so platform staff only — the server re-authorises).
      { label: 'Offline Activation', path: '/ops/offline-activation', icon: 'file-key', staffOnly: true },
    ]},
    SUPPORT_NAV,
  ],
  audit: [
    { group: 'Compliance', items: [
      { label: 'Overview', path: '/audit', icon: 'shield-check' },
      { label: 'Audit Logs', path: '/audit/logs', icon: 'file-text' },
      { label: 'License Events', path: '/audit/lifecycle', icon: 'history' },
    ]},
    { group: 'Evidence', items: [
      { label: 'Overage Evidence', path: '/audit/evidence', icon: 'scale' },
      { label: 'Access Review', path: '/audit/access', icon: 'user-check' },
      { label: 'Validation Logs', path: '/audit/validations', icon: 'badge-check' },
    ]},
    { group: 'Governance', items: [
      { label: 'Roles & Permissions', path: '/audit/roles', icon: 'lock' },
    ]},
    SUPPORT_NAV,
  ],
};

// Derive the persona that owns a route (by path prefix). Shared routes → null.
export function personaForPath(pathname: string): PersonaKey | null {
  if (pathname === '/sales' || pathname.startsWith('/sales/')) return 'sales';
  if (pathname === '/ops' || pathname.startsWith('/ops/')) return 'ops';
  if (pathname === '/audit' || pathname.startsWith('/audit/')) return 'audit';
  return null;
}

// The persona route PREFIX for a path ('/sales' | '/ops' | '/audit'); defaults to
// '/sales' for shared/flat routes. Use this to build cross-view links that keep the
// user in their current workspace (universal, context-preserving navigation).
export function personaBase(pathname: string): string {
  return '/' + (personaForPath(pathname) ?? 'sales');
}

/**
 * A20 W8 — may this user be OFFERED the walkthrough recorder?
 *
 * TWO of the server fence's three conditions, and it is worth being explicit
 * about which one is missing rather than pretending this is the same check.
 * `assertStudioAuthor` (backend/src/services/help-assets.service.ts) demands:
 *
 *   1. `user_profiles.is_admin`      — checked here (isAdmin)
 *   2. `role = 'super_admin'`        — checked here
 *   3. the platform desk (no organization) or an org listed in
 *      STUDIO_DEMO_ORG_IDS — NOT checkable here, because /users/me does not
 *      expose organization_id and STUDIO_DEMO_ORG_IDS is server-side config.
 *
 * That gap is deliberate and safe in the only direction that matters: this
 * predicate can be too PERMISSIVE (a super-admin sitting inside a customer org
 * is offered the recorder and the server then refuses the save with a 403 the
 * panel renders verbatim), never too permissive about the actual write. It
 * cannot be too permissive about the DATA, because it authorises nothing — it
 * decides whether a button is drawn.
 *
 * NOT A SECURITY BOUNDARY. Same standing rule as canMutate and staffOnly: the
 * fence is the server, on the verified session, on every write. A UI gate that
 * anyone can satisfy by editing their own bundle is not a fence, and A18's
 * standing lesson is that treating one as a fence is how they get skipped.
 */
export function mayAuthorWalkthroughs(
  role: UserRole | undefined | null,
  isAdmin: boolean | undefined | null,
): boolean {
  // `=== true` and not a truthiness check: an absent flag must read as false,
  // exactly as the route's `principal()` coerces it. Fail closed on unknown.
  return isAdmin === true && role === 'super_admin';
}

export function workspacesForRole(role: UserRole | undefined | null): PersonaKey[] {
  if (!role) return [];
  return WORKSPACE_ACCESS[role] ?? [];
}

export function defaultWorkspace(role: UserRole | undefined | null): PersonaKey | null {
  return workspacesForRole(role)[0] ?? null;
}

// ── Configurable (data-driven) workspace access ──────────────────────────────
// Mirrors backend/src/lib/rbac.ts exactly. When `permissions` is present the
// workspaces derive from the view.<persona> keys (in persona order); when it is
// null/absent we fall back to the hardcoded WORKSPACE_ACCESS[role] — so existing
// users are net-zero until the matrix is populated + edited, and the unmigrated
// path (no permissions on /users/me) keeps working.
export function workspacesFromPermissions(
  permissions: RolePermissions | null | undefined,
  role: UserRole | undefined | null,
): PersonaKey[] {
  if (permissions == null) return workspacesForRole(role);
  return PERSONA_ORDER.filter((p) => permissions[`view.${p}`] === true);
}

export function defaultWorkspaceFromPermissions(
  permissions: RolePermissions | null | undefined,
  role: UserRole | undefined | null,
): PersonaKey | null {
  return workspacesFromPermissions(permissions, role)[0] ?? null;
}
